import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: attach access token ──────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response: auto-refresh on 401 ─────────────────────────────────────────────
let isRefreshing = false;
let pendingQueue = []; // {resolve, reject}

function processPendingQueue(error, token = null) {
  pendingQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  pendingQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // No refresh token → force logout
      if (!refreshToken) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue subsequent requests while a refresh is in progress
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newToken = data.access_token || data.token;
        localStorage.setItem('auth_token', newToken);
        // Update legacy key too
        localStorage.setItem('token', newToken);

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processPendingQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        processPendingQueue(refreshError, null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── API surface ────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refresh_token: refreshToken }),
};

export const tasksAPI = {
  getTasks: (params) => {
    const processedParams = { ...params };
    if (params.status && Array.isArray(params.status)) processedParams.status = params.status.join(',');
    if (params.priority && Array.isArray(params.priority)) processedParams.priority = params.priority.join(',');
    return api.get('/tasks', { params: processedParams });
  },
  getTask: (id) => api.get(`/tasks/${id}`),
  getFilterOptions: () => api.get('/tasks/filter-options'),
  getTaskHistory: (id) => api.get(`/tasks/${id}/history`),
  updateTaskPriority: (id, prioridad_manual) => api.patch(`/tasks/${id}/priority`, { prioridad_manual }),
  updateStatus: (id, data) => api.put(`/tasks/${id}/status`, data),
  addComment: (id, data) => api.post(`/tasks/${id}/comments`, data),
  uploadImage: (id, data) => api.post(`/tasks/${id}/images`, data),
};

export const syncAPI = {
  syncNow: () => api.post('/sync'),
  getSyncStatus: () => api.get('/sync/status'),
  syncToArcGIS: () => api.post('/sync/arcgis'),
  getArcGISSyncStatus: () => api.get('/sync/arcgis/status'),
  getArcGISSyncLogs: (limit = 10) => api.get('/sync/arcgis/logs', { params: { limit } }),
};

export const reportsAPI = {
  generateReport: (data) => api.post('/reports/generate', data),
};

export const exportAPI = {
  exportToExcel: (filters) => api.post('/export/excel', filters, { responseType: 'blob' }),
};

export const statsAPI = {
  getStatistics: () => api.get('/stats'),
};

export const logsAPI = {
  getApiLogs: (params) => api.get('/logs/api', { params }),
};

// ── AI streaming helpers ───────────────────────────────────────────────────────

const BASE_URL = BACKEND_URL;

export function streamAIResponse(taskId, onChunk, onDone, onError) {
  const token = localStorage.getItem('auth_token');
  return streamSSE(
    `${BASE_URL}/api/ai/tasks/${taskId}/response`,
    'POST',
    null,
    token,
    onChunk,
    onDone,
    onError
  );
}

export function streamExecutiveReport(filters, onChunk, onDone, onError) {
  const token = localStorage.getItem('auth_token');
  return streamSSE(
    `${BASE_URL}/api/ai/reports/executive`,
    'POST',
    { filters },
    token,
    onChunk,
    onDone,
    onError
  );
}

export function streamPerformanceReport(filters, onChunk, onDone, onError) {
  const token = localStorage.getItem('auth_token');
  return streamSSE(
    `${BASE_URL}/api/ai/reports/performance`,
    'POST',
    { filters },
    token,
    onChunk,
    onDone,
    onError
  );
}

/**
 * Generic SSE fetch helper — returns an AbortController so the caller can cancel.
 */
function streamSSE(url, method, body, token, onChunk, onDone, onError) {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(url, {
        method,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });

      if (!res.ok) {
        onError?.(new Error(`HTTP ${res.status}`));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const content = line.slice(6);
            if (content === '[DONE]') {
              onDone?.();
              return;
            }
            if (content.startsWith('[ERROR]')) {
              onError?.(new Error(content.slice(8)));
              return;
            }
            onChunk?.(content);
          }
          // Ignore SSE keep-alive comments (": keep-alive")
        }
      }
      onDone?.();
    } catch (err) {
      if (err.name !== 'AbortError') onError?.(err);
    }
  })();

  return controller;
}

export default api;
